import type { Lang } from "@/lib/i18n";
import type { SubPageContent } from "@/components/common/SubPageLayout";

export const SQUARE_MESH_CONTENT: Record<Lang, SubPageContent> = {
  en: {
    breadcrumbKey: "SquareMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Woven Wire Mesh",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Square Mesh",
      lead: "The foundational category of industrial stainless steel woven wire cloth, defined by precise orthogonal square apertures from 1 to 635 mesh. Engineered for accurate particle classification, fluid straining, and structural sizing.",
      secondaryCta: {
        label: "Woven Wire Mesh Hub",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-stainless-wire-mesh.webp",
        alt: "Close-up macro detail of stainless steel square wire mesh weave",
      },
      cardNote: "Mesh counts 1 to 635 mesh",
      cardLink: {
        label: "Request a Quote",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Mesh Range", value: "1 – 635 Mesh" },
        { label: "Wire Diameters", value: "0.020 – 2.0 mm" },
        { label: "Aperture Range", value: "20 µm – 25 mm" },
        { label: "Standards", value: "ISO 9044 / ASTM E2016" },
      ],
    },
    overview: {
      eyebrow: "Precision Square Apertures",
      heading: "Orthogonal Wire Geometry and Predictable Flow Characteristics",
      paragraphs: [
        "Square mesh represents the primary industrial construction in stainless steel wire weaving. Formed by warp and weft wires interlaced at strict 90-degree angles, it provides open square apertures with uniform aperture distribution across the full roll length.",
        "Depending on the mesh count, aperture size, and required mechanical rigidity, square mesh is produced in either plain weave (1:1) or twilled weave (2:2). This architectural flexibility allows engineers to balance aperture precision, wire diameter, and differential pressure tolerance across chemical, food processing, pharmaceutical, and mineral screening applications.",
        "Fabricated from premium stainless steel grades including AISI 304, AISI 316, and AISI 316L, each weave complies with ISO 9044 industrial wire cloth specifications and ASTM E2016 standard specifications.",
      ],
      keyPoints: [
        "Uniform square apertures across the widest mesh range of woven constructions",
        "Stable orthogonal wire geometry delivering predictable flow and particle retention",
        "Supplied as full rolls, slit coils, custom cut panels, and stamped discs",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
        alt: "Precision plain weave stainless steel wire mesh roll detail",
      },
    },
    specTable: {
      heading: "Standard Square Mesh Reference Specifications",
      intro: "Representative square weave parameters adhering to ASTM E2016 and ISO 9044 industrial tolerances.",
      columns: [
        { key: "mesh", label: "Mesh Count" },
        { key: "weave", label: "Weave Pattern" },
        { key: "aperture", label: "Aperture (µm / mm)", align: "right" },
        { key: "wireDia", label: "Wire Diameter (mm)", align: "right" },
        { key: "openArea", label: "Open Area (%)", align: "right" },
      ],
      rows: [
        { mesh: "2 x 2", weave: "Plain Weave", aperture: "11.1 mm", wireDia: "1.60 mm", openArea: "76.4%" },
        { mesh: "10 x 10", weave: "Plain Weave", aperture: "2.00 mm", wireDia: "0.54 mm", openArea: "62.0%" },
        { mesh: "20 x 20", weave: "Plain Weave", aperture: "850 µm", wireDia: "0.40 mm", openArea: "46.2%" },
        { mesh: "40 x 40", weave: "Plain Weave", aperture: "380 µm", wireDia: "0.25 mm", openArea: "36.4%" },
        { mesh: "100 x 100", weave: "Plain Weave", aperture: "150 µm", wireDia: "0.10 mm", openArea: "36.0%" },
        { mesh: "200 x 200", weave: "Plain / Twill", aperture: "74 µm", wireDia: "0.053 mm", openArea: "33.6%" },
        { mesh: "325 x 325", weave: "Twilled Weave", aperture: "44 µm", wireDia: "0.035 mm", openArea: "31.4%" },
        { mesh: "400 x 400", weave: "Twilled Weave", aperture: "38 µm", wireDia: "0.030 mm", openArea: "36.0%" },
        { mesh: "500 x 500", weave: "Twilled Weave", aperture: "25 µm", wireDia: "0.025 mm", openArea: "25.0%" },
        { mesh: "635 x 635", weave: "Twilled Weave", aperture: "20 µm", wireDia: "0.020 mm", openArea: "25.0%" },
      ],
      caption: "Reference values per ASTM E2016. Custom mesh counts, wire diameters, and slit widths available upon inquiry.",
    },
    features: {
      heading: "Manufacturing and Engineering Capabilities",
      intro: "Precision fabrication options tailored for industrial filtration and separation equipment.",
      items: [
        {
          title: "Rigid Orthogonal Stability",
          text: "Warp and weft crimping prevents wire displacement during continuous vibration, high fluid velocity, or mechanical screening.",
        },
        {
          title: "Alloy Versatility",
          text: "Available in standard AISI 304, marine-grade AISI 316, and low-carbon AISI 316L for optimal corrosion resistance in acidic or saline media.",
        },
        {
          title: "Secondary Conversion",
          text: "Supplied as full rolls up to 2000 mm widths, precision slit coils from 10 mm widths, stamped circular discs, and laser-cut geometries.",
        },
        {
          title: "Surface Cleaning & Degreasing",
          text: "Ultrasonic cleaning and degreasing available when specified in the order for food contact, medical, and high-purity filtration systems.",
        },
      ],
    },
    related: {
      heading: "Square Mesh Weave Architectures",
      intro: "Explore the two primary weaving architectures comprising the square mesh family.",
      cards: [
        {
          title: "Plain Weave Wire Mesh",
          text: "The classic 1:1 alternating pattern delivering maximum open area and square opening precision from 1 to 635 mesh.",
          to: "/PlainWeaveWireMeshPage",
          badge: "1:1 Interlace",
          linkLabel: "Explore Plain Weave",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
            alt: "Plain weave wire mesh roll and aperture detail",
          },
        },
        {
          title: "Twilled Weave Wire Mesh",
          text: "Diagonal 2:2 interlace enabling heavier wire diameters at fine mesh ratings from 100 to 635 mesh for elevated mechanical load.",
          to: "/TwilledWeaveWireMeshPage",
          badge: "2:2 Interlace",
          linkLabel: "Explore Twilled Weave",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/325-Mesh-SS304-Filter-Mesh-Sieve-1.webp",
            alt: "Twilled weave wire mesh micro filter sieve detail",
          },
        },
      ],
    },
    cta: {
      id: "square-mesh-cta",
      eyebrow: "Custom Specification & Supply",
      heading: "Request a Custom Square Mesh Specification",
      lead: "Our technical team assists with mesh count selection, wire diameter optimization, open area calculations, and custom roll or component converting.",
      secondaryLabel: "Browse Woven Wire Mesh Hub",
      secondaryTo: "/WovenWireMeshPage",
      note: "Standard alloys include AISI 304, 316, and 316L. Material test certificates available when specified in the order.",
    },
  },
  zh: {
    breadcrumbKey: "SquareMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "编织丝网系列",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "方孔编织丝网",
      lead: "工业不锈钢编织丝网的核心基石大类，具备高精度的正交方孔几何结构，目数范围覆盖 1 至 635 目。专为颗粒精确分级、固液流体截留及结构筛分工程打造。",
      secondaryCta: {
        label: "返回编织丝网总览",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-stainless-wire-mesh.webp",
        alt: "不锈钢方孔编织丝网微观结构与编织细节",
      },
      cardNote: "目数范围覆盖 1 至 635 目",
      cardLink: {
        label: "获取工程报价",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "目数范围", value: "1 – 635 目" },
        { label: "丝径范围", value: "0.020 – 2.0 mm" },
        { label: "孔径范围", value: "约 20 µm – 25 mm" },
        { label: "制造标准", value: "ISO 9044 / ASTM E2016" },
      ],
    },
    overview: {
      eyebrow: "正交方孔精度",
      heading: "稳固的正交经纬几何结构与可预测的流阻特性",
      paragraphs: [
        "方孔编织网是不锈钢金属丝编织中最通用的基础构型。经丝与纬丝以严格的 90 度正交角度交织，在整卷丝网表面形成规则一致的正方形孔径分布。",
        "根据具体目数、孔径大小以及机械负荷承受需求，方孔网采用 1:1 平纹编织或 2:2 斜纹编织工艺。这种结构灵活性使工程设计人员能够在化工、食品加工、制药与矿业颗粒筛分工况中平衡开孔率、抗变形强度与截留精度。",
        "产品主要选用 AISI 304、AISI 316 及 AISI 316L 等优质不锈钢牌号，严格遵循 ISO 9044 工业金属丝织物公差与 ASTM E2016 行业技术规范。",
      ],
      keyPoints: [
        "正交方孔开孔高度均匀，覆盖编织丝网中最广泛的目数与孔径范围",
        "经纬交织几何结构稳固，流动阻力特性清晰且颗粒截留精确可预测",
        "提供原卷、分条卷带、定制切片与冲压滤圆片等多种交货规格",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
        alt: "精密平纹不锈钢丝网卷材细节",
      },
    },
    specTable: {
      heading: "标准方孔编织丝网参考规格表",
      intro: "代表性方孔网编织技术参数，符合 ASTM E2016 与 ISO 9044 工业制造公差要求。",
      columns: [
        { key: "mesh", label: "网孔目数 (Mesh)" },
        { key: "weave", label: "编织形式" },
        { key: "aperture", label: "网孔孔径 (µm / mm)", align: "right" },
        { key: "wireDia", label: "金属丝径 (mm)", align: "right" },
        { key: "openArea", label: "开孔率 (%)", align: "right" },
      ],
      rows: [
        { mesh: "2 x 2", weave: "平纹编织", aperture: "11.1 mm", wireDia: "1.60 mm", openArea: "76.4%" },
        { mesh: "10 x 10", weave: "平纹编织", aperture: "2.00 mm", wireDia: "0.54 mm", openArea: "62.0%" },
        { mesh: "20 x 20", weave: "平纹编织", aperture: "850 µm", wireDia: "0.40 mm", openArea: "46.2%" },
        { mesh: "40 x 40", weave: "平纹编织", aperture: "380 µm", wireDia: "0.25 mm", openArea: "36.4%" },
        { mesh: "100 x 100", weave: "平纹编织", aperture: "150 µm", wireDia: "0.10 mm", openArea: "36.0%" },
        { mesh: "200 x 200", weave: "平纹 / 斜纹", aperture: "74 µm", wireDia: "0.053 mm", openArea: "33.6%" },
        { mesh: "325 x 325", weave: "斜纹编织", aperture: "44 µm", wireDia: "0.035 mm", openArea: "31.4%" },
        { mesh: "400 x 400", weave: "斜纹编织", aperture: "38 µm", wireDia: "0.030 mm", openArea: "36.0%" },
        { mesh: "500 x 500", weave: "斜纹编织", aperture: "25 µm", wireDia: "0.025 mm", openArea: "25.0%" },
        { mesh: "635 x 635", weave: "斜纹编织", aperture: "20 µm", wireDia: "0.020 mm", openArea: "25.0%" },
      ],
      caption: "参考数据基于 ASTM E2016 规范。如需定制非标目数、特定丝径或特殊切边分条，欢迎详询。",
    },
    features: {
      heading: "制造工艺与深度加工能力",
      intro: "专为工业流体过滤与颗粒筛分设备匹配的精密制造选项。",
      items: [
        {
          title: "正交交织稳定性",
          text: "精密的经纬预弯与锁紧工艺，避免连续振动筛分、高速冲刷或机械拉伸时发生丝线位移。",
        },
        {
          title: "合金材料多样性",
          text: "涵盖常规 AISI 304、耐腐蚀 AISI 316 及超低碳抗晶间腐蚀 AISI 316L 等多种牌号。",
        },
        {
          title: "深加工与分切",
          text: "支持宽幅整卷（最宽可达 2000 mm）、窄带精密分条（最小 10 mm）、激光切割异形件及冲压滤圆片。",
        },
        {
          title: "表面清洗与脱脂",
          text: "可按订单技术要求提供超声波清洗脱脂处理，满足食品医药及高洁净过滤设备要求。",
        },
      ],
    },
    related: {
      heading: "方孔网编织结构分类",
      intro: "深入了解构成方孔丝网系列的两种核心织造形式。",
      cards: [
        {
          title: "平纹编织丝网",
          text: "经典 1:1 一上一下经纬交织形式，提供开孔率与方孔均匀度，目数覆盖 1 至 635 目。",
          to: "/PlainWeaveWireMeshPage",
          badge: "1:1 正交编织",
          linkLabel: "查看平纹网详情",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
            alt: "平纹编织不锈钢丝网微观与卷材细节",
          },
        },
        {
          title: "斜纹编织丝网",
          text: "对角 2:2 二上二下错位编织，允许在 100 至 635 细目数下采用更粗丝径，耐压抗变形能力更强。",
          to: "/TwilledWeaveWireMeshPage",
          badge: "2:2 斜纹编织",
          linkLabel: "查看斜纹网详情",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/325-Mesh-SS304-Filter-Mesh-Sieve-1.webp",
            alt: "斜纹编织微孔过滤筛网细节",
          },
        },
      ],
    },
    cta: {
      id: "square-mesh-cta",
      eyebrow: "定制规格与批量供应",
      heading: "获取方孔编织丝网技术方案与工程报价",
      lead: "我们的技术支持团队可协助您完成目数选型、丝径优化、开孔率核算及分条冲片定制加工。",
      secondaryLabel: "返回编织丝网总览",
      secondaryTo: "/WovenWireMeshPage",
      note: "标准牌号包含 AISI 304、316 与 316L。材质证明与检测报告可按订单要求提供。",
    },
  },
  es: {
    breadcrumbKey: "SquareMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Malla metálica tejida",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malla cuadrada",
      lead: "Categoría principal de tela metálica tejida de acero inoxidable, definida por aberturas cuadradas ortogonales de 1 a 635 mesh. Diseñada para clasificación de partículas, filtración de fluidos y cribado estructural.",
      secondaryCta: {
        label: "Malla tejida general",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-stainless-wire-mesh.webp",
        alt: "Detalle macro de tejido de malla de alambre cuadrada de acero inoxidable",
      },
      cardNote: "Gama de 1 a 635 mesh",
      cardLink: {
        label: "Solicitar cotización",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Rango de malla", value: "1 – 635 Mesh" },
        { label: "Diámetros de alambre", value: "0,020 – 2,0 mm" },
        { label: "Rango de abertura", value: "20 µm – 25 mm" },
        { label: "Normas", value: "ISO 9044 / ASTM E2016" },
      ],
    },
    overview: {
      eyebrow: "Aberturas cuadradas de precisión",
      heading: "Geometría ortogonal estable y comportamiento de flujo predecible",
      paragraphs: [
        "La malla cuadrada representa la construcción industrial básica del tejido de alambre de acero inoxidable. Al entrelazar hilos de urdimbre y trama en ángulos estrictos de 90 grados, ofrece aberturas cuadradas regulares a lo largo de todo el rollo.",
        "Dependiendo del número de mesh y de la rigidez mecánica requerida, se fabrica en tejido liso (1:1) o cruzado (2:2). Esta flexibilidad permite optimizar el caudal, la retención de partículas y la resistencia a la presión en aplicaciones químicas, farmacéuticas y alimentarias.",
        "Producida en aceros inoxidables AISI 304, AISI 316 y AISI 316L conforme a las normas ISO 9044 y ASTM E2016.",
      ],
      keyPoints: [
        "Aberturas cuadradas uniformes en la gama más amplia de tejidos de alambre",
        "Geometría ortogonal estable con flujo predecible y retención constante",
        "Suministro en rollos completos, bobinas cortadas, paneles y discos estampados",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
        alt: "Detalle de rollo de malla cuadrada de acero inoxidable",
      },
    },
    specTable: {
      heading: "Especificaciones estándar de referencia para malla cuadrada",
      intro: "Parámetros representativos de tejido cuadrado según tolerancias industriales ASTM E2016 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Número de malla (Mesh)" },
        { key: "weave", label: "Tipo de ligamento" },
        { key: "aperture", label: "Abertura (µm / mm)", align: "right" },
        { key: "wireDia", label: "Diámetro de alambre (mm)", align: "right" },
        { key: "openArea", label: "Área abierta (%)", align: "right" },
      ],
      rows: [
        { mesh: "2 x 2", weave: "Tejido liso", aperture: "11,1 mm", wireDia: "1,60 mm", openArea: "76,4%" },
        { mesh: "10 x 10", weave: "Tejido liso", aperture: "2,00 mm", wireDia: "0,54 mm", openArea: "62,0%" },
        { mesh: "20 x 20", weave: "Tejido liso", aperture: "850 µm", wireDia: "0,40 mm", openArea: "46,2%" },
        { mesh: "40 x 40", weave: "Tejido liso", aperture: "380 µm", wireDia: "0,25 mm", openArea: "36,4%" },
        { mesh: "100 x 100", weave: "Tejido liso", aperture: "150 µm", wireDia: "0,10 mm", openArea: "36,0%" },
        { mesh: "200 x 200", weave: "Liso / Cruzado", aperture: "74 µm", wireDia: "0,053 mm", openArea: "33,6%" },
        { mesh: "325 x 325", weave: "Tejido cruzado", aperture: "44 µm", wireDia: "0,035 mm", openArea: "31,4%" },
        { mesh: "400 x 400", weave: "Tejido cruzado", aperture: "38 µm", wireDia: "0,030 mm", openArea: "36,0%" },
        { mesh: "500 x 500", weave: "Tejido cruzado", aperture: "25 µm", wireDia: "0,025 mm", openArea: "25,0%" },
        { mesh: "635 x 635", weave: "Tejido cruzado", aperture: "20 µm", wireDia: "0,020 mm", openArea: "25,0%" },
      ],
      caption: "Valores de referencia según ASTM E2016. Especificaciones especiales disponibles bajo consulta.",
    },
    features: {
      heading: "Capacidades de fabricación y conformado",
      intro: "Opciones de mecanizado y corte adaptadas a sistemas de filtración industrial.",
      items: [
        {
          title: "Estabilidad estructural ortogonal",
          text: "El ondulado controlado evita el desplazamiento del alambre durante la vibración constante o el flujo a alta velocidad.",
        },
        {
          title: "Aleaciones estándar",
          text: "Disponibilidad en aceros AISI 304, AISI 316 y bajo carbono AISI 316L para distintos grados de agresividad química.",
        },
        {
          title: "Corte y transformación",
          text: "Suministro en rollos de hasta 2000 mm, tiras estrechas desde 10 mm, formas por corte láser y discos estampados.",
        },
        {
          title: "Limpieza y desengrase",
          text: "Procesos de limpieza por ultrasonidos y desengrase disponibles cuando se especifiquen en el pedido.",
        },
      ],
    },
    related: {
      heading: "Estructuras de tejido de malla cuadrada",
      intro: "Conozca las dos formas de ligamento que componen la gama de malla cuadrada.",
      cards: [
        {
          title: "Tejido liso",
          text: "Patrón alternado 1:1 que proporciona máxima regularidad en aberturas cuadradas de 1 a 635 mesh.",
          to: "/PlainWeaveWireMeshPage",
          badge: "Ligamento 1:1",
          linkLabel: "Ver tejido liso",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
            alt: "Detalle de malla metálica de tejido liso",
          },
        },
        {
          title: "Tejido cruzado",
          text: "Entrecruzamiento 2:2 que permite alambres de mayor calibre en mallas finas de 100 a 635 mesh bajo carga.",
          to: "/TwilledWeaveWireMeshPage",
          badge: "Ligamento 2:2",
          linkLabel: "Ver tejido cruzado",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/325-Mesh-SS304-Filter-Mesh-Sieve-1.webp",
            alt: "Malla de filtración en tejido cruzado",
          },
        },
      ],
    },
    cta: {
      id: "square-mesh-cta",
      eyebrow: "Suministro y personalización",
      heading: "Solicite una especificación personalizada de malla cuadrada",
      lead: "Nuestro equipo técnico puede asesorarle en la selección del número de mesh, cálculo de área abierta y corte a medida.",
      secondaryLabel: "Volver a malla tejida",
      secondaryTo: "/WovenWireMeshPage",
      note: "Aleaciones estándar AISI 304, 316 y 316L. Certificados de material disponibles cuando se especifiquen en el pedido.",
    },
  },
  de: {
    breadcrumbKey: "SquareMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Drahtgewebe",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Quadratmaschengewebe",
      lead: "Die primäre Kategorie industrieller Edelstahl-Drahtgewebe mit präzisen rechtwinkligen Quadratmaschen von 1 bis 635 Mesh. Entwickelt für exakte Partikelklassierung, Flüssigkeitsfiltration und Schutzsiebung.",
      secondaryCta: {
        label: "Drahtgewebe Übersicht",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-stainless-wire-mesh.webp",
        alt: "Makroaufnahme von Edelstahl-Quadratmaschendrahtgewebe",
      },
      cardNote: "Maschenbereich 1 bis 635 Mesh",
      cardLink: {
        label: "Angebot anfordern",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Maschenbereich", value: "1 – 635 Mesh" },
        { label: "Drahtdurchmesser", value: "0,020 – 2,0 mm" },
        { label: "Maschenweite", value: "20 µm – 25 mm" },
        { label: "Normen", value: "ISO 9044 / ASTM E2016" },
      ],
    },
    overview: {
      eyebrow: "Präzise Quadratmaschen",
      heading: "Rechtwinklige Drahtgeometrie und reproduzierbare Durchflusscharakteristik",
      paragraphs: [
        "Quadratmaschengewebe stellen die am häufigsten eingesetzte Konstruktionsart industrieller Edelstahl-Drahtgewebe dar. Durch die orthogonale Verwebung von Kettdrähten und Schussdrähten im 90-Grad-Winkel entstehen über die gesamte Rollenlänge gleichmäßige quadratische Öffnungen.",
        "Je nach Maschenzahl und geforderter Stabilität wird Quadratmaschengewebe in Leinwandbindung (1:1) oder Köperbindung (2:2) gefertigt. Dadurch lässt sich die Balance zwischen offener Siebfläche, Drahtstärke und Druckbeständigkeit gezielt anpassen.",
        "Gefertigt aus den Werkstoffen AISI 304, AISI 316 und AISI 316L gemäß ISO 9044 und ASTM E2016.",
      ],
      keyPoints: [
        "Gleichmäßige quadratische Maschenöffnungen über das breiteste Spezifikationsspektrum",
        "Stabile rechtwinklige Drahtgeometrie für berechenbaren Durchfluss und reproduzierbaren Rückhalt",
        "Lieferung als Rollenware, Spaltbänder, Passzuschnitte und gestanzte Ronden",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
        alt: "Rolle aus Edelstahl-Quadratmaschengewebe",
      },
    },
    specTable: {
      heading: "Referenzspezifikationen für Quadratmaschengewebe",
      intro: "Ausgewählte Gewebeparameter gemäß Fertigungstoleranzen nach ASTM E2016 und ISO 9044.",
      columns: [
        { key: "mesh", label: "Maschenzahl (Mesh)" },
        { key: "weave", label: "Bindungsart" },
        { key: "aperture", label: "Maschenweite (µm / mm)", align: "right" },
        { key: "wireDia", label: "Drahtdurchmesser (mm)", align: "right" },
        { key: "openArea", label: "Offene Siebfläche (%)", align: "right" },
      ],
      rows: [
        { mesh: "2 x 2", weave: "Leinwandbindung", aperture: "11,1 mm", wireDia: "1,60 mm", openArea: "76,4%" },
        { mesh: "10 x 10", weave: "Leinwandbindung", aperture: "2,00 mm", wireDia: "0,54 mm", openArea: "62,0%" },
        { mesh: "20 x 20", weave: "Leinwandbindung", aperture: "850 µm", wireDia: "0,40 mm", openArea: "46,2%" },
        { mesh: "40 x 40", weave: "Leinwandbindung", aperture: "380 µm", wireDia: "0,25 mm", openArea: "36,4%" },
        { mesh: "100 x 100", weave: "Leinwandbindung", aperture: "150 µm", wireDia: "0,10 mm", openArea: "36,0%" },
        { mesh: "200 x 200", weave: "Leinwand / Köper", aperture: "74 µm", wireDia: "0,053 mm", openArea: "33,6%" },
        { mesh: "325 x 325", weave: "Köperbindung", aperture: "44 µm", wireDia: "0,035 mm", openArea: "31,4%" },
        { mesh: "400 x 400", weave: "Köperbindung", aperture: "38 µm", wireDia: "0,030 mm", openArea: "36,0%" },
        { mesh: "500 x 500", weave: "Köperbindung", aperture: "25 µm", wireDia: "0,025 mm", openArea: "25,0%" },
        { mesh: "635 x 635", weave: "Köperbindung", aperture: "20 µm", wireDia: "0,020 mm", openArea: "25,0%" },
      ],
      caption: "Referenzwerte gemäß ASTM E2016. Kundenspezifische Abmessungen auf Anfrage lieferbar.",
    },
    features: {
      heading: "Fertigungs- und Konfektionsmöglichkeiten",
      intro: "Präzise Verarbeitungsmöglichkeiten für industrielle Filtrationssysteme.",
      items: [
        {
          title: "Orthogonale Formstabilität",
          text: "Präzise Drahtverkröpfung verhindert Maschenverzug bei kontinuierlicher Vibration oder starker Durchströmung.",
        },
        {
          title: "Legierungsoptionen",
          text: "Lieferbar in AISI 304, korrosionsbeständigem AISI 316 sowie kohlenstoffarmem AISI 316L.",
        },
        {
          title: "Konfektionierung",
          text: "Breiten bis 2000 mm als Vollrollen, Spaltbänder ab 10 mm Breite, Laserzuschnitte und Stanzronden.",
        },
        {
          title: "Ultraschallreinigung",
          text: "Entfettung und Reinigung im Ultraschallbad verfügbar, wenn im Auftrag spezifiziert.",
        },
      ],
    },
    related: {
      heading: "Webarten des Quadratmaschengewebes",
      intro: "Erfahren Sie mehr über die beiden grundlegenden Bindungsarten.",
      cards: [
        {
          title: "Leinwandbindung",
          text: "Das klassische 1:1-Gewebe für gleichmäßige Maschenweiten und hohe Durchflussraten von 1 bis 635 Mesh.",
          to: "/PlainWeaveWireMeshPage",
          badge: "1:1 Bindung",
          linkLabel: "Leinwandbindung ansehen",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
            alt: "Leinwandgewebe Detailansicht",
          },
        },
        {
          title: "Köperbindung",
          text: "Diagonale 2:2-Köperbindung für stärkere Drähte bei feinen Maschenweiten von 100 bis 635 Mesh.",
          to: "/TwilledWeaveWireMeshPage",
          badge: "2:2 Bindung",
          linkLabel: "Köperbindung ansehen",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/325-Mesh-SS304-Filter-Mesh-Sieve-1.webp",
            alt: "Köpergewebe Filterstruktur",
          },
        },
      ],
    },
    cta: {
      id: "square-mesh-cta",
      eyebrow: "Spezifikation & Fertigung",
      heading: "Quadratmaschengewebe anfragen",
      lead: "Unsere Ingenieure beraten Sie gerne bei der Maschenauswahl, Drahtoptimierung und Zuschnittfertigung.",
      secondaryLabel: "Zur Drahtgewebe-Übersicht",
      secondaryTo: "/WovenWireMeshPage",
      note: "Standardwerkstoffe AISI 304, 316 und 316L. Werksprüfzeugnisse auf Kundenanforderung verfügbar.",
    },
  },
  fr: {
    breadcrumbKey: "SquareMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Toiles métalliques tissées",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Toile carrée",
      lead: "Catégorie fondamentale des toiles métalliques en acier inoxydable, caractérisée par des ouvertures carrées orthogonales précises de 1 à 635 mesh. Conçue pour la séparation granulométrique et la filtration de fluides.",
      secondaryCta: {
        label: "Aperçu toiles tissées",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-stainless-wire-mesh.webp",
        alt: "Détail macro du tissage de toile métallique carrée en acier inoxydable",
      },
      cardNote: "Plage de mailles de 1 à 635 mesh",
      cardLink: {
        label: "Demander un devis",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Plage de mailles", value: "1 – 635 Mesh" },
        { label: "Diamètres de fil", value: "0,020 – 2,0 mm" },
        { label: "Ouvertures", value: "20 µm – 25 mm" },
        { label: "Normes", value: "ISO 9044 / ASTM E2016" },
      ],
    },
    overview: {
      eyebrow: "Ouvertures carrées régulières",
      heading: "Stabilité orthogonale et comportement d'écoulement prévisible",
      paragraphs: [
        "La toile métallique carrée constitue la structure industrielle de référence dans le tissage de fils d'acier inoxydable. En entrelaçant les fils de chaîne et de trame à angle droit, elle assure une régularité dimensionnelle constante sur toute la surface tissée.",
        "Selon le nombre de mailles et la résistance mécanique requise, elle est tissée en armure unie (1:1) ou sergée (2:2). Cette flexibilité permet d'équilibrer surface ouverte, épaisseur de fil et tolérance à la pression différentielle.",
        "Fabriquée à partir d'aciers inoxydables AISI 304, AISI 316 et AISI 316L conformément aux normes industrielles ISO 9044 et ASTM E2016.",
      ],
      keyPoints: [
        "Ouvertures carrées homogènes sur la plus large plage de mailles industrielles",
        "Géométrie orthogonale stable garantissant un débit fluide et une rétention prévisibles",
        "Fournie en rouleaux complets, bandes refendues, panneaux découpés et disques estampés",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
        alt: "Rouleau de toile métallique carrée en acier inoxydable",
      },
    },
    specTable: {
      heading: "Spécifications types de toiles métalliques carrées",
      intro: "Paramètres représentatifs selon les tolérances de fabrication ASTM E2016 et ISO 9044.",
      columns: [
        { key: "mesh", label: "Nombre de mailles (Mesh)" },
        { key: "weave", label: "Armure de tissage" },
        { key: "aperture", label: "Ouverture (µm / mm)", align: "right" },
        { key: "wireDia", label: "Diamètre de fil (mm)", align: "right" },
        { key: "openArea", label: "Surface ouverte (%)", align: "right" },
      ],
      rows: [
        { mesh: "2 x 2", weave: "Tissage uni", aperture: "11,1 mm", wireDia: "1,60 mm", openArea: "76,4%" },
        { mesh: "10 x 10", weave: "Tissage uni", aperture: "2,00 mm", wireDia: "0,54 mm", openArea: "62,0%" },
        { mesh: "20 x 20", weave: "Tissage uni", aperture: "850 µm", wireDia: "0,40 mm", openArea: "46,2%" },
        { mesh: "40 x 40", weave: "Tissage uni", aperture: "380 µm", wireDia: "0,25 mm", openArea: "36,4%" },
        { mesh: "100 x 100", weave: "Tissage uni", aperture: "150 µm", wireDia: "0,10 mm", openArea: "36,0%" },
        { mesh: "200 x 200", weave: "Uni / Sergé", aperture: "74 µm", wireDia: "0,053 mm", openArea: "33,6%" },
        { mesh: "325 x 325", weave: "Tissage sergé", aperture: "44 µm", wireDia: "0,035 mm", openArea: "31,4%" },
        { mesh: "400 x 400", weave: "Tissage sergé", aperture: "38 µm", wireDia: "0,030 mm", openArea: "36,0%" },
        { mesh: "500 x 500", weave: "Tissage sergé", aperture: "25 µm", wireDia: "0,025 mm", openArea: "25,0%" },
        { mesh: "635 x 635", weave: "Tissage sergé", aperture: "20 µm", wireDia: "0,020 mm", openArea: "25,0%" },
      ],
      caption: "Valeurs indicatives selon ASTM E2016. Découpes sur mesure disponibles sur demande.",
    },
    features: {
      heading: "Capacités de fabrication et de transformation",
      intro: "Finitions industrielles pour l'intégration directe dans les équipements de filtration.",
      items: [
        {
          title: "Rigidité orthogonale",
          text: "L'ondulation maîtrisée prévient le glissement des fils sous vibration continue ou flux hydraulique intense.",
        },
        {
          title: "Choix des nuances",
          text: "Disponible en AISI 304, AISI 316 résistant à la corrosion et AISI 316L bas carbone.",
        },
        {
          title: "Format et découpe",
          text: "Rouleaux jusqu'à 2000 mm de largeur, bandes refendues dès 10 mm, découpe laser et disques estampés.",
        },
        {
          title: "Nettoyage ultrasons",
          text: "Dégraissage et nettoyage aux ultrasons possibles lorsque stipulés dans la commande.",
        },
      ],
    },
    related: {
      heading: "Types de tissage de toile carrée",
      intro: "Découvrez les deux architectures de tissage composant la famille des toiles carrées.",
      cards: [
        {
          title: "Tissage uni",
          text: "Structure 1:1 classique offrant une régularité dimensionnelle et une perméabilité élevée de 1 à 635 mesh.",
          to: "/PlainWeaveWireMeshPage",
          badge: "Armure 1:1",
          linkLabel: "Découvrir le tissage uni",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
            alt: "Toile métallique à tissage uni",
          },
        },
        {
          title: "Tissage sergé",
          text: "Entrecroisement 2:2 permettant l'emploi de fils plus épais sur mailles fines de 100 à 635 mesh sous forte contrainte.",
          to: "/TwilledWeaveWireMeshPage",
          badge: "Armure 2:2",
          linkLabel: "Découvrir le tissage sergé",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/325-Mesh-SS304-Filter-Mesh-Sieve-1.webp",
            alt: "Toile métallique sergée haute résistance",
          },
        },
      ],
    },
    cta: {
      id: "square-mesh-cta",
      eyebrow: "Conseil & Commande",
      heading: "Demandez une étude pour votre toile carrée",
      lead: "Nos spécialistes vous conseillent sur le choix des mailles, le diamètre des fils et les options de découpe.",
      secondaryLabel: "Retour aux toiles tissées",
      secondaryTo: "/WovenWireMeshPage",
      note: "Nuances AISI 304, 316 et 316L. Certificats matière disponibles lorsque spécifiés dans la commande.",
    },
  },
  pt: {
    breadcrumbKey: "SquareMesh",
    relatedTopic: "woven",
    hero: {
      categoryBadge: "Tela metálica tecida",
      standardBadge: "ISO 9044 / ASTM E2016",
      heading: "Malha quadrada",
      lead: "Categoria fundamental de tela de arame de aço inoxidável, caracterizada por aberturas quadradas ortogonais de 1 a 635 mesh. Projetada para classificação de partículas, filtragem de fluidos e peneiramento estrutural.",
      secondaryCta: {
        label: "Visão geral de telas",
        to: "/WovenWireMeshPage",
      },
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/09/plain-stainless-wire-mesh.webp",
        alt: "Detalhe macro da trama de malha quadrada de arame de aço inoxidável",
      },
      cardNote: "Faixa de 1 a 635 mesh",
      cardLink: {
        label: "Solicitar cotação",
        to: "/RequestAQuotePage",
      },
      specs: [
        { label: "Faixa de malha", value: "1 – 635 Mesh" },
        { label: "Diâmetros de arame", value: "0,020 – 2,0 mm" },
        { label: "Faixa de abertura", value: "20 µm – 25 mm" },
        { label: "Normas", value: "ISO 9044 / ASTM E2016" },
      ],
    },
    overview: {
      eyebrow: "Aberturas quadradas de precisão",
      heading: "Geometria ortogonal estável e comportamento de fluxo previsível",
      paragraphs: [
        "A malha quadrada representa a estrutura industrial básica no tecido de arame de aço inoxidável. Ao entrelaçar fios de urdume e trama em ângulos exatos de 90 graus, proporciona aberturas quadradas uniformes em todo o rolo.",
        "Dependendo do número de mesh e da rigidez mecânica exigida, é produzida em ponto liso (1:1) ou ponto sarjado (2:2). Essa flexibilidade permite conciliar área aberta, diâmetro de arame e resistência sob pressão.",
        "Fabricada em aços inoxidáveis AISI 304, AISI 316 e AISI 316L de acordo com as normas ISO 9044 e ASTM E2016.",
      ],
      keyPoints: [
        "Aberturas quadradas uniformes na faixa mais ampla de telas tecidas",
        "Geometria ortogonal estável com comportamento de fluxo previsível e retenção confiável",
        "Fornecida em rolos inteiros, fitas cortadas, painéis sob medida e discos estampados",
      ],
      image: {
        src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
        alt: "Rolo de tela de arame de malha quadrada em aço inoxidável",
      },
    },
    specTable: {
      heading: "Especificações de referência de malha quadrada",
      intro: "Parâmetros representativos de acordo com tolerâncias industriais ASTM E2016 e ISO 9044.",
      columns: [
        { key: "mesh", label: "Contagem de malha (Mesh)" },
        { key: "weave", label: "Padrão de tecedura" },
        { key: "aperture", label: "Abertura (µm / mm)", align: "right" },
        { key: "wireDia", label: "Diâmetro do arame (mm)", align: "right" },
        { key: "openArea", label: "Área aberta (%)", align: "right" },
      ],
      rows: [
        { mesh: "2 x 2", weave: "Ponto liso", aperture: "11,1 mm", wireDia: "1,60 mm", openArea: "76,4%" },
        { mesh: "10 x 10", weave: "Ponto liso", aperture: "2,00 mm", wireDia: "0,54 mm", openArea: "62,0%" },
        { mesh: "20 x 20", weave: "Ponto liso", aperture: "850 µm", wireDia: "0,40 mm", openArea: "46,2%" },
        { mesh: "40 x 40", weave: "Ponto liso", aperture: "380 µm", wireDia: "0,25 mm", openArea: "36,4%" },
        { mesh: "100 x 100", weave: "Ponto liso", aperture: "150 µm", wireDia: "0,10 mm", openArea: "36,0%" },
        { mesh: "200 x 200", weave: "Liso / Sarjado", aperture: "74 µm", wireDia: "0,053 mm", openArea: "33,6%" },
        { mesh: "325 x 325", weave: "Ponto sarjado", aperture: "44 µm", wireDia: "0,035 mm", openArea: "31,4%" },
        { mesh: "400 x 400", weave: "Ponto sarjado", aperture: "38 µm", wireDia: "0,030 mm", openArea: "36,0%" },
        { mesh: "500 x 500", weave: "Ponto sarjado", aperture: "25 µm", wireDia: "0,025 mm", openArea: "25,0%" },
        { mesh: "635 x 635", weave: "Ponto sarjado", aperture: "20 µm", wireDia: "0,020 mm", openArea: "25,0%" },
      ],
      caption: "Valores de referência conforme ASTM E2016. Dimensões customizadas sob consulta.",
    },
    features: {
      heading: "Capacidades de fabricação e acabamento",
      intro: "Opções de transformação sob medida para filtros e peneiras industriais.",
      items: [
        {
          title: "Estabilidade ortogonal",
          text: "A conformação controlada evita o deslocamento dos arames sob vibração contínua ou fluxo de alta velocidade.",
        },
        {
          title: "Opções de liga",
          text: "Disponível em AISI 304, AISI 316 para ambientes corrosivos e AISI 316L com baixo teor de carbono.",
        },
        {
          title: "Corte e conformação",
          text: "Rolos de até 2000 mm de largura, fitas a partir de 10 mm, cortes a laser e discos estampados.",
        },
        {
          title: "Limpeza ultrassônica",
          text: "Desengraxe e limpeza ultrassônica disponíveis quando especificados no pedido.",
        },
      ],
    },
    related: {
      heading: "Estruturas de tecedura de malha quadrada",
      intro: "Conheça os dois padrões de trama que compõem a linha de malha quadrada.",
      cards: [
        {
          title: "Ponto liso",
          text: "Trama 1:1 clássica com abertura regular e excelente permeabilidade de 1 a 635 mesh.",
          to: "/PlainWeaveWireMeshPage",
          badge: "Trama 1:1",
          linkLabel: "Ver ponto liso",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/10/heor-image-of-plain-weave-wire-mesh-1-1.webp",
            alt: "Tela de arame em ponto liso",
          },
        },
        {
          title: "Ponto sarjado",
          text: "Entrelaçamento diagonal 2:2 que viabiliza arames mais espessos em malhas finas de 100 a 635 mesh sob pressão.",
          to: "/TwilledWeaveWireMeshPage",
          badge: "Trama 2:2",
          linkLabel: "Ver ponto sarjado",
          image: {
            src: "https://mutualfilter.com/wp-content/uploads/2026/09/325-Mesh-SS304-Filter-Mesh-Sieve-1.webp",
            alt: "Tela de filtragem em ponto sarjado",
          },
        },
      ],
    },
    cta: {
      id: "square-mesh-cta",
      eyebrow: "Engenharia e fornecimento",
      heading: "Solicite especificações de malha quadrada",
      lead: "Nossa equipe técnica auxilia na seleção de mesh, diâmetro de arame e cálculos de área aberta para sua aplicação.",
      secondaryLabel: "Voltar para telas tecidas",
      secondaryTo: "/WovenWireMeshPage",
      note: "Ligas padrão AISI 304, 316 e 316L. Certificados de matéria-prima disponíveis quando especificados no pedido.",
    },
  },
};
